import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useApi } from '../api-context';
import { queryKeys } from '../query-keys';
import {
  cabinetProfileResultSchema,
  type CabinetProfileResult,
  type UpdateCabinetProfileInput,
} from '../schemas';
import { useToken } from './use-token';

/**
 * L'identité du cabinet : ce qui s'applique, ce qui a été saisi, d'où ça vient.
 *
 * Lecture ouverte à tous les membres — l'aperçu des messages en a besoin.
 * Les écritures ci-dessous sont réservées aux admins du cabinet : l'API répond
 * `403` sinon.
 */
export function useCabinetProfile() {
  const api = useApi();
  const token = useToken();

  return useQuery({
    queryKey: queryKeys.cabinet.profile(),
    queryFn: () =>
      api.get<CabinetProfileResult>('/cabinets/profile', { schema: cabinetProfileResultSchema }),
    enabled: token !== null,
  });
}

/**
 * Enseigne, téléphone, adresse.
 *
 * Vider un champ ne le laisse pas vide : il repasse à ce que Doctolib
 * rapporte. C'est ce qui permet de corriger une fois, puis de laisser le relevé
 * reprendre la main.
 */
export function useUpdateCabinetProfile() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: UpdateCabinetProfileInput) =>
      api.patch<CabinetProfileResult>('/cabinets/profile', input, {
        schema: cabinetProfileResultSchema,
      }),
    onSuccess: () => invalider(queryClient),
  });
}

/** Dépose ou retire le logo du cabinet. `null` le retire. */
export function useSaveCabinetLogo() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (logo: string | null) =>
      api.put<CabinetProfileResult>(
        '/cabinets/logo',
        { logo },
        { schema: cabinetProfileResultSchema },
      ),
    onSuccess: () => invalider(queryClient),
  });
}

/**
 * L'identité du cabinet se lit à trois endroits : ses réglages, la signature
 * par défaut des e-mails, et l'aperçu des messages. Les trois se rafraîchissent
 * ensemble, sinon l'un montre encore l'ancienne enseigne.
 */
function invalider(queryClient: ReturnType<typeof useQueryClient>): void {
  void queryClient.invalidateQueries({ queryKey: queryKeys.cabinet.all() });
  void queryClient.invalidateQueries({ queryKey: queryKeys.emailSettings.all() });
  void queryClient.invalidateQueries({ queryKey: queryKeys.auth.me() });
}
